import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings, Shield, BarChart3, Megaphone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Link } from "react-router-dom";
import { DEFAULT_CONSENT, ESSENTIAL_ONLY, getConsent, setConsent } from "../lib/consent";

const translations = {
  es: {
    title: "Usamos cookies",
    description: "Utilizamos cookies para mejorar tu experiencia. Puedes aceptar todas o personalizar tus preferencias.",
    acceptAll: "Aceptar todas",
    rejectAll: "Solo esenciales",
    customize: "Personalizar",
    savePreferences: "Guardar preferencias",
    moreInfo: "Más información",
    essential: "Esenciales",
    essentialDesc: "Necesarias para el funcionamiento",
    analytics: "Analíticas",
    analyticsDesc: "Nos ayudan a mejorar",
    preferences: "Preferencias",
    preferencesDesc: "Recuerdan tu configuración",
    marketing: "Publicidad",
    marketingDesc: "Medición de campañas (TikTok)",
  },
  en: {
    title: "We use cookies",
    description: "We use cookies to improve your experience. You can accept all or customize your preferences.",
    acceptAll: "Accept all",
    rejectAll: "Essential only",
    customize: "Customize",
    savePreferences: "Save preferences",
    moreInfo: "More info",
    essential: "Essential",
    essentialDesc: "Required for functionality",
    analytics: "Analytics",
    analyticsDesc: "Help us improve",
    preferences: "Preferences",
    preferencesDesc: "Remember your settings",
    marketing: "Advertising",
    marketingDesc: "Campaign measurement (TikTok)",
  }
};

export default function CookieBanner({ language = "es" }) {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_CONSENT);

  const t = translations[language] || translations.es;

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = getConsent();
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const applyConsent = (prefs) => {
    setConsent(prefs);
    setIsVisible(false);
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => applyConsent(DEFAULT_CONSENT);
  const handleRejectAll = () => applyConsent(ESSENTIAL_ONLY);
  const handleSavePreferences = () => applyConsent(preferences);

  const togglePreference = (key) => {
    if (key === 'essential') return; // Cannot disable essential
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
        data-testid="cookie-banner"
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-neutral-900/95 backdrop-blur-xl border border-neutral-800 rounded-2xl shadow-2xl overflow-hidden">
            {/* Main Banner */}
            {!showCustomize ? (
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1">{t.title}</h3>
                    <p className="text-neutral-400 text-sm mb-4">{t.description}</p>
                    
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={handleAcceptAll}
                        className="bg-blue-500 hover:bg-blue-400 text-white rounded-full px-6 font-semibold"
                        data-testid="cookie-accept-all"
                      >
                        {t.acceptAll}
                      </Button>
                      <Button
                        onClick={handleRejectAll}
                        variant="outline"
                        className="rounded-full px-6 border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                        data-testid="cookie-reject-all"
                      >
                        {t.rejectAll}
                      </Button>
                      <Button
                        onClick={() => setShowCustomize(true)}
                        variant="ghost"
                        className="rounded-full px-6 text-neutral-400 hover:text-white hover:bg-neutral-800"
                        data-testid="cookie-customize"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        {t.customize}
                      </Button>
                    </div>
                  </div>
                  <button 
                    onClick={handleRejectAll}
                    className="text-neutral-500 hover:text-white transition-colors p-1"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              /* Customize Panel */
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-white">{t.customize}</h3>
                  <button 
                    onClick={() => setShowCustomize(false)}
                    className="text-neutral-500 hover:text-white transition-colors p-1"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Essential */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-neutral-800/50 border border-neutral-700">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-blue-400" />
                      <div>
                        <div className="font-semibold text-white">{t.essential}</div>
                        <div className="text-xs text-neutral-500">{t.essentialDesc}</div>
                      </div>
                    </div>
                    <Switch checked={true} disabled className="opacity-50" />
                  </div>

                  {/* Analytics */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-neutral-800/50 border border-neutral-700">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="w-5 h-5 text-blue-400" />
                      <div>
                        <div className="font-semibold text-white">{t.analytics}</div>
                        <div className="text-xs text-neutral-500">{t.analyticsDesc}</div>
                      </div>
                    </div>
                    <Switch 
                      checked={preferences.analytics} 
                      onCheckedChange={() => togglePreference('analytics')}
                      data-testid="cookie-toggle-analytics"
                    />
                  </div>

                  {/* Preferences */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-neutral-800/50 border border-neutral-700">
                    <div className="flex items-center gap-3">
                      <Settings className="w-5 h-5 text-blue-400" />
                      <div>
                        <div className="font-semibold text-white">{t.preferences}</div>
                        <div className="text-xs text-neutral-500">{t.preferencesDesc}</div>
                      </div>
                    </div>
                    <Switch 
                      checked={preferences.preferences} 
                      onCheckedChange={() => togglePreference('preferences')}
                      data-testid="cookie-toggle-preferences"
                    />
                  </div>

                  {/* Marketing */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-neutral-800/50 border border-neutral-700">
                    <div className="flex items-center gap-3">
                      <Megaphone className="w-5 h-5 text-blue-400" />
                      <div>
                        <div className="font-semibold text-white">{t.marketing}</div>
                        <div className="text-xs text-neutral-500">{t.marketingDesc}</div>
                      </div>
                    </div>
                    <Switch 
                      checked={preferences.marketing} 
                      onCheckedChange={() => togglePreference('marketing')}
                      data-testid="cookie-toggle-marketing"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={handleSavePreferences}
                    className="bg-blue-500 hover:bg-blue-400 text-white rounded-full px-6 font-semibold"
                    data-testid="cookie-save-preferences"
                  >
                    {t.savePreferences}
                  </Button>
                  <Link to="/cookies">
                    <Button
                      variant="ghost"
                      className="rounded-full px-6 text-neutral-400 hover:text-white hover:bg-neutral-800"
                    >
                      {t.moreInfo}
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
